import { useQuery } from '@tanstack/react-query';
import { CATEGORY_ICON, Icon, Spinner, type CategoryCode } from '@barev/ui';
import { useI18n, useTRPC } from '@barev/web';
import { ClubTicket, LEVEL_NAME, levelDiscount, QrGlyph, Stars, Timeline, toneClass, useOrderTimeline, useParamsSummary, type Level } from '../lib.tsx';

/**
 * Главная для тех, кто уже живёт в Армении.
 * Сверху — сливовый блок как на сайте: приветствие и клубная карта.
 * Дальше — «Сейчас» (ближайший заказ со шкалой), просьба оценить, направления, чат.
 */
export function Home() {
  const { t, dt } = useI18n();
  const trpc = useTRPC();
  const me = useQuery(trpc.client.me.queryOptions());
  const orders = useQuery({ ...trpc.client.orders.list.queryOptions(), refetchInterval: 20_000 });
  const catalog = useQuery(trpc.client.catalog.queryOptions());
  const timeline = useOrderTimeline();
  const summary = useParamsSummary();
  const level = (me.data?.membership.level ?? 'BAREV') as Level;
  const now = orders.data?.active[0];
  const moreActive = (orders.data?.active.length ?? 0) - 1;
  const toRate = orders.data?.history.find((o) => o.done && !o.rating);

  return (
    <main className="app app--hero">
      <section className="hero">
        <div className="hero__word" aria-hidden="true">
          Barev
        </div>
        <h1 className="hero__hello">
          <small>{t('client.home.hello')}</small>
          <span>{me.data?.firstName ?? t('client.home.guest')}</span>
        </h1>
        <ClubTicket
          level={level}
          code={me.data?.cardCode}
          href="#/perks"
          until={
            level !== 'BAREV' && me.data?.membership.endsAt
              ? t('client.home.levelUntil', { plan: LEVEL_NAME[level], date: dt(me.data.membership.endsAt, { day: 'numeric', month: 'long' }) })
              : t('client.home.free')
          }
          action={
            <>
              <QrGlyph />
              <span>{t('client.home.cardShow')}</span>
            </>
          }
        />
      </section>

      {orders.isPending ? (
        <Spinner />
      ) : now ? (
        <section className="sect">
          <div className="sect__head">
            <h2 className="sect__title">
              <span className="live-dot" aria-hidden="true" />
              {t('client.home.now')}
            </h2>
            <a href="#/orders" className="sect__link">
              {t('client.home.allOrders')}
            </a>
          </div>
          <a href={`#/orders/${now.id}`} className="now-card">
            <span className="now-card__top">
              <span className={`order-card__icon ${toneClass(now.category)}`}>
                <Icon name={CATEGORY_ICON[now.category as CategoryCode] ?? 'home'} size={22} />
              </span>
              <span className="now-card__main">
                <b>{t(`services.${now.serviceCode}.name`)}</b>
                <span>{summary(now.serviceCode, now.params)}</span>
              </span>
              <Icon name="arrow" size={20} />
            </span>
            <span className="now-card__when">
              <Icon name="clock" size={18} />
              {dt(now.scheduledAt, { weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' })}
            </span>
            <Timeline items={timeline(now)} compact />
          </a>
          {moreActive > 0 && (
            <a href="#/orders" className="more-link">
              {t('client.home.moreActive', { count: moreActive })}
              <Icon name="arrow" size={16} />
            </a>
          )}
        </section>
      ) : (
        <section className="empty-now">
          <p>{t('client.home.noOrders')}</p>
          <a href="#/order/cleaning" className="pill pill--dark">
            <Icon name="plus" size={18} strokeWidth={2.6} />
            {t('services.cleaning.name')}
          </a>
        </section>
      )}

      {toRate && (
        <a href={`#/orders/${toRate.id}?rate=1`} className="rate-nudge">
          <span>
            <b>{t('client.rate.prompt')}</b>
            <span>
              {t(`services.${toRate.serviceCode}.name`)} · {dt(toRate.scheduledAt, { day: 'numeric', month: 'short' })}
            </span>
          </span>
          <Stars value={0} size={22} />
        </a>
      )}

      <section className="sect">
        <div className="sect__head">
          <h2 className="sect__title">{t('client.home.orderTitle')}</h2>
          <a href="#/services" className="sect__link">
            {t('client.nav.services')}
          </a>
        </div>
        <div className="tiles">
          {(catalog.data?.categories ?? []).map((c) => (
            <a key={c.code} href={c.code === 'PERKS' ? '#/perks' : `#/services?c=${c.code}`} className={`tile ${toneClass(c.code)}`}>
              <Icon name={CATEGORY_ICON[c.code as CategoryCode]} size={26} />
              <span className="tile__title">{t(`categories.${c.code}.name`)}</span>
              <span className="tile__note">{levelDiscount(level, c) > 0 ? t('client.services.yourDiscount', { pct: levelDiscount(level, c) }) : t(`categories.${c.code}.short`)}</span>
            </a>
          ))}
        </div>
      </section>

      {level === 'BAREV' && (
        <a href="#/membership" className="upsell">
          <b>{t('client.home.upsellTitle', { pct: me.data?.plans.eliteMaxPct ?? 0 })}</b>
          <span className="bc-small">{t('client.home.upsellText')}</span>
        </a>
      )}

      <a href="#/chat" className="chat-cta">
        <span>
          <b>{t('client.home.chatTitle')}</b>
          <span>{t('client.home.chatText')}</span>
        </span>
        <span className="pill pill--green">
          <Icon name="chat" size={18} />
          {t('client.nav.chat')}
        </span>
      </a>
    </main>
  );
}
